import type { ApiDeliveryDeal } from 'src/types/delivery';
import type { ApiDirectPurchaseItem } from 'src/types/directPurchase';

import { DeliveryDealStatus } from 'src/types/delivery';

/**
 * "Did *I* buy this car?" — the question a sold listing has to answer for the person who bought it
 * (contracts v3, UI part §4).
 *
 * A buyer who follows their own link, or reopens the tab they bought from, currently reads "This
 * car has already been sold" about their own purchase. The copy is correct and useless: the one
 * visitor who most needs a receipt and a way back to their deal is the one told the least.
 *
 * Strangers keep seeing exactly what they see now — this only fires on the viewer's own record.
 */

/** Statuses in which the buyer really is the one who took this car off the market. */
const OWNING_STATUSES: DeliveryDealStatus[] = [
  DeliveryDealStatus.Escrowed,
  DeliveryDealStatus.Disputed,
  DeliveryDealStatus.PickedUp,
  DeliveryDealStatus.Delivered,
  DeliveryDealStatus.HandedOver,
];

export type OwnPurchase =
  | { kind: 'delivery'; deal: ApiDeliveryDeal }
  | { kind: 'direct'; purchase: ApiDirectPurchaseItem };

interface FindParams {
  listingId: string;
  deliveryDeals: ApiDeliveryDeal[] | undefined;
  directPurchases: ApiDirectPurchaseItem[] | undefined;
}

/**
 * The viewer's own purchase of this listing, if there is one.
 *
 * An offer that was withdrawn, declined or expired does not count: nothing was bought, the car is
 * unavailable for somebody else's reason, and claiming otherwise would be worse than the plain
 * message. A pending Buy Now does not count either — until the transfer is verified the purchase
 * may still fail, and a receipt for it would be a promise the chain has not made.
 *
 * The escrow is preferred when both exist, because it is the one with a deal to walk back into.
 */
export function findOwnPurchase({ listingId, deliveryDeals, directPurchases }: FindParams): OwnPurchase | null {
  const deal = (deliveryDeals ?? [])
    .filter((candidate) => candidate.listingId === listingId && OWNING_STATUSES.includes(candidate.status))
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0];

  if (deal) {
    return { kind: 'delivery', deal };
  }

  const purchase = (directPurchases ?? [])
    .filter((candidate) => candidate.listingId === listingId && candidate.status === 'verified')
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0];

  return purchase ? { kind: 'direct', purchase } : null;
}
